/**
 * Loading a single resource: not found vs. busy vs. failed.
 *
 * A detail page used to say "not found" for every error, including the API's
 * 429 rate limit — telling a user a challenge they can see in the list does
 * not exist, when the server was only asking them to wait.
 */

/**
 * Seconds to wait from a Retry-After header value (delta-seconds or an
 * HTTP-date), or null when absent or unparseable. Never negative.
 */
export function parseRetryAfter(value: string | null | undefined, nowMs: number = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - nowMs) / 1000));
}

/**
 * An RTK Query error with the response's Retry-After attached by the
 * endpoint's transformErrorResponse (the hook does not expose headers).
 */
export interface LoadError {
  status?: number | string;
  data?: unknown;
  retryAfterSeconds?: number | null;
}

/** transformErrorResponse helper: keep the error, add Retry-After when present. */
export function withRetryAfter<E extends object>(
  error: E,
  meta: { response?: Response } | undefined
): E & { retryAfterSeconds: number | null } {
  const header = meta?.response?.headers?.get?.('Retry-After') ?? null;
  return { ...error, retryAfterSeconds: parseRetryAfter(header) };
}

export type LoadErrorKind = 'not_found' | 'rate_limited' | 'other';

/**
 * Why loading one resource failed. 404 (and 400/422: a malformed or invalid
 * id) is "not found"; 429 is "busy — try again shortly", never "not found";
 * anything else (5xx, network) is a failure to load, not a missing resource.
 */
export function classifyLoadError(error: unknown): { kind: LoadErrorKind; retryAfterSeconds: number | null } {
  const e = error as LoadError | undefined;
  if (e?.status === 404 || e?.status === 400 || e?.status === 422) return { kind: 'not_found', retryAfterSeconds: null };
  if (e?.status === 429) {
    return { kind: 'rate_limited', retryAfterSeconds: typeof e.retryAfterSeconds === 'number' ? e.retryAfterSeconds : null };
  }
  return { kind: 'other', retryAfterSeconds: null };
}
